<template>
  <div
    class="details-container"
    style="overflow: hidden"
    @click.stop="$emit('close')"
  >
      <div class="d-flex flex-column" style="background: #FFFFFF; height: 95vh; width: 780px; margin: auto; overflow: hidden; border-radius: .5em" @click.stop="">
          <header class="flex-shrink-0 py-3">
              <div
                class="col-12 mb-2 pb-2"
                style="border-bottom: #d9d9d9 1px solid"
              >
                <div class="d-flex justify-content-end">
                    <input
                        type="checkbox"
                        class="form-check-input"
                        v-model="showAllPhotos"
                        id="showAllPhotosCheckbox"
                    >
                    <span class="ms-2 me-4">{{ $t('observations.show_all_photos') }}</span>
                    <button class="btn btn-sm btn-secondary mx-1" @click.stop="$emit('close')">{{ $t('common.close') }}</button>
                    <button class="btn btn-sm btn-primary mx-1" :disabled="loading" @click.stop="generatePdf">{{ $t('common.print') }}</button>
                </div>
              </div>
          </header>
          <main ref="mainContent" class="flex-grow-1 overflow-auto p-3" style="overflow-x: hidden !important;">
              <div class="print-page">
                  <div class="row">
                      <div class="col-6 nopads">
                          <h1 class="report-header">
                              {{ getReportTitle() }}
                          </h1>
                          <div class="report-header-date">
                              <span class="report-header-date-label">{{ $t('work_assignment.generation_date_label') }}</span>
                              {{ getCurrentDay() }}
                          </div>
                          <div class="report-header-date">
                              <span class="report-header-date-label">{{ $t('work_assignment.workassignment_period_label') }}</span>
                              {{ formatWorkassignmentPeriod(assignments) }}
                          </div>
                      </div>
                      <div
                          class="col-6 nopads"
                          style="text-align: right"
                      >
                          <img
                              src="../../assets/routa_dark.jpg"
                              class="print-logo"
                              alt="logo"
                          >
                      </div>
                  </div>
                  <hr>
                  <!-- Summary map -->
                  <div class="col-12">
                      <div class="map-container summary-map-container">
                          <map-container
                              ref="summaryMapContainer"
                              id="summaryMap"
                              :find-user="false"
                              @onMapChanged="mapChanged"
                          />
                      </div>
                  </div>
                  <div class="px-3" style="font-size: 1.5em">{{ assignments.length }} {{ $t('work_assignment.count') }}</div>
              </div>

              <work-assignment-print-details
                v-for="assignment in assignments"
                :ref="'assignment_'+assignment.id"
                :key="assignment.id"
                :assignment="assignment"
                :report-completed="reportCompleted"
                :show-all-photos="showAllPhotos"
                :user="user"
                @remove="removeItem"
              />
          </main>
          <footer class="flex-shrink-0 p-3 border-top">
              <div class="d-flex justify-content-between">
                  <button
                      class="btn btn-sm btn-outline-secondary"
                      @click.stop="scrollToTop"
                  >
                      ↑
                  </button>

                  <button
                      class="btn btn-sm btn-outline-secondary"
                      @click.stop="scrollToBottom"
                  >
                      ↓
                  </button>
              </div>
          </footer>
    </div>
    <div
      v-if="loading"
      id="loader"
      class="spinner"
    />
  </div>
</template>

<script>
import {timeUtils} from "@/components/mixins/TimeUtils";
import MapContainer from "@/components/map/MapContainer";
import {PdfGeneratorManager} from "@/modules/PdfGeneratorManager";
import {restApi} from "@/components/mixins/RestApiMixin";
import {mapHelper} from '../mixins/MapMixin'
import Vue3Datatable from '@bhplugin/vue3-datatable'
import {imageCacheService} from "@/services/ImageCacheService";
import {pdfMixin} from "@/components/mixins/PdfMixin";
import WorkAssignmentPrintDetails from "./WorkAssignmentPrintDetails.vue";
import {workAssignmentHelper} from "../mixins/WorkAssignmentMixin.js";

export default {
    name: 'WorkAssignmentDetailsReport',
    components: {WorkAssignmentPrintDetails, MapContainer, Vue3Datatable},
    mixins: [timeUtils, restApi, mapHelper, pdfMixin, workAssignmentHelper],
    props: {
        assignments: {
            type: Array,
            default() {
                return []
            }
        },
        user: {
            type: Object,
            default: null
        },
        companyPreferences: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            map: null,
            reportCompleted: false,
            showAllPhotos: false,
            loading: false,
            companyLogo: null,
            GROUP_ID: 0,
            Z_INDEX: {
                BEHIND: 2,
                INFRONT: 5
            },
            items: [],
        }
    },
    beforeUnmount() {
        // Allways clear the cache to free memory
        imageCacheService.clear();
    },
    watch: {
        map(val) {
            if (val) {
                this.initView()
            }
        }
    },
    methods: {
        scrollToTop() {
            const container = this.$refs.mainContent;
            if (container) {
                container.scrollTo({
                    top: 0,
                    behavior: 'smooth'
                });
            }
        },

        scrollToBottom() {
            const container = this.$refs.mainContent;
            if (container) {
                container.scrollTo({
                    top: container.scrollHeight,
                    behavior: 'smooth'
                });
            }
        },

        getReportTitle() {
            const companyPart = this.user.company && this.user.company.display_name ? this.user.company.display_name : "Routa"
            let title = this.$t('work_assignment.pdf_title')
            return companyPart + ' - ' + title
        },

        async generatePdf() {
            this.loading = true

            const {
                pdfDoc, pdfService, fonts, colors, footerLogo, companyFooterLogo, pdfGeneratorManager
            } = await this.initializePdf()

            const { fontText, fontBold, fontHeader } = fonts
            const { textColor, headerColor, boldColor } = colors

            const screenshot = await this.map.takeScreenShot()
            const summaryMap = await new Response(screenshot).arrayBuffer()


            let nextYPosition = await this.addPdfHeaderWithMap(this.getReportTitle(), summaryMap, null, null, this.formatWorkassignmentPeriod(this.assignments))
            const tablePage = pdfDoc.getPage(pdfDoc.getPages().length -1)

            nextYPosition -= 10
            nextYPosition = pdfService.addText(pdfDoc, this.assignments.length + ' ' + this.$t('work_assignment.count'), fontHeader, 16, nextYPosition, undefined, undefined, undefined, footerLogo, companyFooterLogo, headerColor)

            const afterTablePage = pdfDoc.getPage(pdfDoc.getPages().length -1)

            if (afterTablePage === tablePage) {
                nextYPosition -= 500
            }

            nextYPosition -= 10

            for (let i = 0; i < this.assignments.length; i++) {
                const assignment = this.assignments[i]
                const photos = await this.fetchPhotos(assignment)
                nextYPosition = await this.addAssignmentToPdf(assignment, photos, nextYPosition, i)
                // nextYPosition is now updated for the next iteration
            }

            const pdfBytes = await pdfGeneratorManager.generateBytesFromPdfDoc(pdfDoc)
            const fileName = this.$t('work_assignment.pdf_title');
            PdfGeneratorManager.downloadPdf(pdfBytes, this.user && this.user.company && this.user.company.display_name && this.user.company.display_name !== "" ? this.user.company.display_name + " - " + this.$t('common.report') : fileName)
            this.loading = false
        },

        isPng(photo) {
            return photo.startsWith('data:image/png;');
        },

        async takePhotoOfMap(id) {
            let item = this.$refs['assignment_' + id]
            if (Array.isArray(item)) {
                item = item[0]
            }
            const result = item.getMarkerEditorMap()
            if (result) {
                const ss = await result.takeScreenShot()
                return await new Response(ss).arrayBuffer();
            }
            return null
        },

        async fetchPhotos(assignment) {
            const id = assignment.id
            const results = []
            let item = this.$refs['assignment_' + id]
            if (Array.isArray(item)) {
                item = item[0]
            }
            const photos = item.getPhotos()
            for (let i = 0; i < photos.length; i++) {
                const photo = photos[i]
                // now we check the cache first - cache ids set in WorkAssignmentPrintDetails!
                const photoId = id + '_' + i
                if (imageCacheService.has(photoId)) {
                    results.push(imageCacheService.get(photoId));
                } else if (photo.src) {
                    results.push(photo.src);
                } else {
                    const response = await new Promise(resolve => this.restFetch(photo.url, resolve))
                    results.push(response.data)
                }
            }
            return results
        },

        mapChanged: function (map) {
            this.map = map
        },

        initView() {
            // Draw assignments on the map
            if (this.map) {
                //Remove any previous assignments
                this.map.removeMapItemsByType(this.WORK_ASSIGNMENT)
                //Draw the new assignments
                this.drawAssignments()
                this.$nextTick(() =>  {
                    this.map.zoomToGroup(this.WORK_ASSIGNMENT)
                })
            }
        },

        drawAssignments: function () {
            this.assignments.forEach(assignment => {
                const point = this.getWorkAssignmentPointPosition(assignment)
                if (point) {
                    this.map.showMapMarker(assignment.id, this.WORK_ASSIGNMENT, point.y, point.x)
                }
            })
        },
        removeItem(assignmentId) {
            const index = this.assignments.findIndex(item => item.id === assignmentId)
            if (index >= 0) {
                this.assignments.splice(index, 1)
                this.initView()
            }
        }
    }
}
</script>

<style scoped>
.report-header {
    margin: 0;
    padding-top: .2em;
    font-size: 1.2em;
    font-weight: bold;
    text-transform: uppercase;
}

.report-header-date {
    display: block;
    width: 100%;
    white-space: normal;
    word-break: break-word;
}

.report-header-date-label {
    display: inline;
    font-weight: bold;
    margin-right: .3em;
}

.summary-map-container {
    position: relative;
    border: 1px solid #d9d9d9;
    border-radius: 1em;
    overflow: hidden;
    width: 750px;
    height: 350px;
    margin: auto;
    margin-bottom: .5em;
}

</style>

